<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import QRCode from '@chenfengyuan/vue-qrcode'
import { VDataTableServer } from "vuetify/labs/VDataTable"

import { router } from '@/plugins/2.router'
import EpisodeService from "@/services/episode.service"
import EpisodeDrawer from '@/views/feedback/ticket-management/drawer/episodeDrawer.vue'
import { computed, watch } from "vue"

import echo from "@/@core/utils/echo"
import { NotificationChannel } from '@/enums/global/notification.channel'
import { NotificationType } from '@/enums/global/notification.type'

const searchQuery = ref("")
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: "app-master-episode",
    subject: 'feedback.menu',
    action: 'episode',
  },
})

const snackbar = useToast()

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Form type filter
const { data: formTypes } = await useApi(
  createUrl("masters/form-types", {
    query: {
      select: ["id", "name"],
    },
  }),
)

const formTypeFilter = ref()
const visitDateRangeFilter = ref([])
const dischargeDateRangeFilter = ref([])

// Headers
const headers = [
  { title: '', key: 'data-table-expand' },
  {
    title: "View",
    key: "view",
    sortable: false,
  },
  {
    title: "Action",
    key: "action",
    sortable: false,
  },
  {
    title: "Code",
    key: "code",
  },
  {
    title: "Patient",
    key: "patient",
  },
  {
    title: "Doctor",
    key: "doctor_name",
  },
  {
    title: "Form Type",
    key: "form_type.name",
  },
  {
    title: "Location",
    key: "location.name",
  },
  {
    title: "Visited Date",
    key: "visit_date_time",
  },
  {
    title: "Discharge Date",
    key: "discharge_date",
  },
  {
    title: "WA Invitation",
    key: "wa_invitation",
  },
]

const { data: episodeListing, execute: fetchEpisodeListing } = await useApi(
  createUrl("/masters/episodes", {
    query: {
      paged: 1,
      q: searchQuery,
      itemsPerPage,
      page,
      sort: sortBy,
      order_by: orderBy,
      search: {
        unit_id: unitId,
        form_type_id: formTypeFilter,
        visit_date_time: visitDateRangeFilter,
        discharge_date: dischargeDateRangeFilter,
      },
    },
  }),
)

const isEpisodeDrawerVisible = ref(false)

const  customPropertiesTableData = ref([])
const patienDetailsTableData = ref([])  
const episodeDetaisTableData = ref([])

const getCustomPropertiesByEpisodeId = episodeId => {
  const episode = episodeListing.value?.data.find(item => item.id === episodeId)

  if (episode?.custom_properties && typeof episode.custom_properties === 'object') {
    customPropertiesTableData.value  = Object.entries(episode.custom_properties).map(([key, val]) => ({
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      value: val ?? '',
    }))
  }else {
    customPropertiesTableData.value = []
  }

  patienDetailsTableData.value = [
    { label: 'Name', value: episode?.customer?.name ?? '' },
    { label: 'Phone', value: episode?.customer?.phone ?? '' },
    { label: 'Uhid', value: episode?.customer?.uhid ?? '' },
    { label: 'Email', value: episode?.customer?.email ?? '' },
    { label: 'Age', value: episode?.customer?.age ?? '' },
    { label: 'Code', value: episode?.customer?.code ?? '' },
    { label: 'Date of Birth', value: episode?.customer?.date_of_birth ?? '' },
  ]

  episodeDetaisTableData.value = [
    { label: 'Doctor Name', value: episode?.doctor_name ?? '' },
    { label: 'Speciality', value: episode?.speciality ?? '' },
    { label: 'Status', value: episode?.status ?? '' },
    { label: 'Code', value: episode?.code ?? '' },
    { label: 'Location', value: episode?.location?.name ?? '' },
    { label: 'Form Type', value: episode?.form_type?.name ?? '' },
    { label: 'Visit Date', value: episode?.visit_date_time ?? '' },
    { label: 'Discharge Date', value: episode?.discharge_date ?? '' },
  ]
  isEpisodeDrawerVisible.value = true
}





const categories = computed(() => episodeListing.value?.data)
const totalCategories = computed(() => episodeListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const investigationGroupData = ref()

const showEpisode = (data = null, viewOnly = false) => {
  investigationGroupData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    investigationGroupData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  investigationGroupData.value = data
  isConfirmDialogVisible.value = true
}

const deleteEpisode = async (confirm, data) => {
  if (data?.id && confirm) {
    await EpisodeService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchEpisodeListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) investigationGroupData.value = null
})

const refImportForm = ref()
const importFile = ref()
const importErrors = ref()
const importStatus = ref({})
const isImportProcessing = ref(false)
const isImportLoading = ref(false)
const isImportModal = ref(false)

const submitImport = async () => {
  isImportLoading.value = true
  await EpisodeService.import(
    {
      file: importFile.value,
    },
    () => {
      importErrors.value = null
      isImportProcessing.value = true
    },
    error => {
      importErrors.value = error.data.errors
    },
  )
  isImportLoading.value = false
}

// const formatImportErrors = errors => {
//   if (!errors) return []

//   const groupedErrors = Object.entries(errors).reduce(
//     (acc, [key, messages]) => {
//       const [row, column] = key.split(".")
//       const displayRow = Number(row) + 1
//       if (!acc[row]) acc[row] = []
//       acc[row].push(
//         ...messages.map(message => ({
//           title: `Row No ${displayRow}: ${column
//             .replace(/_/g, " ")
//             .replace(/^\w/, c => c.toUpperCase())} ${message
//             .replace(`The ${key} field`, "")
//             .trim()}`,

//           // title: `In row ${row} ${column.replace(/_/g, ' ').replace(/^\w/, c => c.toLowerCase())} column`,
//           // message: message.replace(`The ${key} field`, 'Field')
//         })),
//       )
      
//       return acc
//     },
//     {},
//   )

//   return Object.keys(groupedErrors)
//     .sort((a, b) => Number(a) - Number(b)) // Sort rows in numerical order
//     .flatMap(row => groupedErrors[row])
// }

const downloadSampleFile = async () => {
  await downloadFile(
    "masters/episodes/sample-import-file/export",
    "episode-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportModal.value,
  value => {
    if (value === false) {
      importFile.value = null
      importErrors.value = null
    }
  },
)

watch(
  () => importStatus.value,
  value => {
    switch (value.status) {
    case "completed":
      importFile.value = null
      importErrors.value = null
      importStatus.value = {}
      isImportProcessing.value = false
      isImportLoading.value = false
      isImportModal.value = false
      fetchEpisodeListing()
      snackbar.success("Episodes imported successfully")
      break

    case "validation_failed":
      importErrors.value = value.errors
      importStatus.value = {}
      isImportProcessing.value = false
      isImportLoading.value = false
      snackbar.error("Excel file validation failed")
      break

    case "failed":
      importErrors.value = value.errors
      importStatus.value = {}
      isImportProcessing.value = false
      isImportLoading.value = false
      snackbar.error("Error during importing episodes")
      break

    default:
      // do nothing
      break
    }
  },
  { deep: true },
)

echo
  .private("episode-import-status")
  .listen("EpisodeImportStatusEvent", e => {
    console.log(e)
    importStatus.value = e
  })

// const channel = echo.private("test-channel").connect();
// console.log(echo)

// echo.private(`test-channel`)
//     .listen('BroadTestEvent', (data) => {
//         console.log('Event Data:', data);
//     });

//     echo.private(`test-channel`).listen("BroadTestEvent",
//   (e) => console.log(e)
// );

const isFeedbackCollectDialog = ref(false)
const feedbackCollectForm = ref({ feedback_type: 'standard' })

const selectedFeedbackType = computed(() => feedbackCollectForm.value.feedback_type)

const { 
  data: sourceTypes,
  isFetching: sourceTypesLoading,
} = useApi(createUrl('masters/feedback-sources', { 
  query: { 
    select: ['id', 'name', 'icon'],
    search: {
      source_type: selectedFeedbackType,
    },
  },
}), {
  afterFetch: () => {
    feedbackCollectForm.value.source_type_id = null
  },
})

const { 
  isFetching: locationLoading,
  data: locations, 
} = useListFetchingService('/masters/locations', {
  query: {
    search: {
      unit_id: unitId,
    },
  },
})

const { 
  isFetching: notificationTemplateLoading,
  data: notificationTemplates, 
} = useListFetchingService('/masters/notification-templates', {
  query: {
    unit_id: unitId,
    notification_type: NotificationType.EXTERNAL,
    notification_channel: NotificationChannel.WHATSAPP,
  },
})

const sendWhatsAppInvitation = async () => {
  await $api(`/feedback/episode/send-whatsapp-invitation`, {
    method: 'POST',
    body: {      
      episode_id: feedbackCollectForm.value.episode.id,
      form_type_id: feedbackCollectForm.value.form_type_id,
      location_id: feedbackCollectForm.value.location_id,
      notification_template_id: feedbackCollectForm.value.notification_template_id,
    },
  }).then(({ data }) => {
    snackbar.success('Feedback Invitation Successfully')
  
    return data
  }).catch(error => {
    return error
  })
}

const submitFeedbackCollect = () => {
  const url = router.resolve({
    name: 'app-feedback-collection',
    params: {
      type: feedbackCollectForm.value.feedback_type,
    },
    query: {
      episode: feedbackCollectForm.value.episode.id,
      source_type: feedbackCollectForm.value.source_type_id,
      form_type: feedbackCollectForm.value.form_type_id,
      location: feedbackCollectForm.value.location_id,
    },
  })

  window.open(url.href, '_blank')
}

const qrCodeRef = ref()
const qrCodeLink = ref()

watch(() => feedbackCollectForm.value, value => {
  if (
    value.episode &&
    value.source_type_id &&
    value.form_type_id &&
    value.location_id
  ) {
    let url = import.meta.env.VITE_BASE_URL +'/feedback-collect?'

    url += `&u=${btoa(unitId.value)}`
    url += `&e=${btoa(value.episode.id)}`
    url += `&s=${btoa(value.source_type_id)}`
    url += `&f=${btoa(value.form_type_id)}`
    url += `&l=${btoa(value.location_id)}`

    qrCodeLink.value = url
  } else {
    qrCodeLink.value = null
  }
}, { deep: true })

const onQRReady = canvas => {
  const context = canvas.getContext('2d')
  const image = new Image()

  // Add form type name to the qr code at the bottom
  context.font = 'bold 15px Arial'
  context.fillStyle = '#000'
  context.textAlign = 'center'
  context.height = '512px'
  context.width = '512px'
}

const downloadQRCode = () => {
  if (!qrCodeRef.value) return

  const canvas = qrCodeRef.value.$el
  const dataURL = canvas.toDataURL('image/png')
  const link = document.createElement('a')

  link.href = dataURL
  link.download = 'qrcode.png'
  link.click()
}

watch(() => isFeedbackCollectDialog.value, value => {
  if (value === false) {
    qrCodeLink.value = null
    feedbackCollectForm.value = { feedback_type: 'standard' }
  }
})
</script>

<template>
  <section>
    <VCard>
      <VCardText>
        <VRow>
          <VCol cols="4">
            <AppSelect
              v-model="formTypeFilter"
              :items="formTypes"
              :item-title="(item) => item.name"
              :item-value="(item) => item.id"
              label="Form Type"
              placeholder="Select Form Type"
              :return-object="false"
              :clearable="true"
            />
          </VCol>
          <VCol cols="4">
            <AppDateTimePicker
              label="Visited Date"
              placeholder="Select Date"
              :config="{
                mode: 'range',
                dateFormat: 'Y-m-d',
                maxDate: new Date(),
                onChange: (selectedDates, dateStr, instance) => {
                  console.log(selectedDates)
                  visitDateRangeFilter = [
                    dateTimePickerDateFormat(selectedDates[0]),
                    dateTimePickerDateFormat(selectedDates[1])
                  ]
                },
              }"
              :clearable="true"
            />
          </VCol>
          <VCol cols="4">
            <AppDateTimePicker
              label="Discharge Date"
              placeholder="Select Date"
              :config="{
                mode: 'range',
                dateFormat: 'Y-m-d',
                maxDate: new Date(),
                onChange: (selectedDates, dateStr, instance) => {
                  dischargeDateRangeFilter = [
                    dateTimePickerDateFormat(selectedDates[0]),
                    dateTimePickerDateFormat(selectedDates[1])
                  ]
                },
              }"
              :clearable="true"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VCard class="mt-6">
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->

          <VBtn
            v-if="$can('create', 'master.form_type')"
            prepend-icon="tabler-plus"
            @click="isImportModal = !isImportModal"
          >
            Import Episode
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="categories"
        :items-length="totalCategories"
        :headers="headers"
        :fixed-header="true"
        class="text-no-wrap"
        expand-on-click
        @update:options="updateOptions"
      >
        <template #expanded-row="slotProps">
          <tr class="v-data-table__tr evel-table-custom-bg-color">
            <td
              colspan="12"
              class="level-bg-color pb-4"
            >
              <VCard
                v-if="slotProps.item.feedbacks.length > 0"
                variant="text"
                class="my-8 mx-10"
              >
                <Table 
                  class="border mt-1"
                  :columns="[
                    { key: 'created_at', title: 'Collected Date' },
                    { key: 'status', title: 'Status' },
                    { key: 'actions', title: 'Actions' },
                  ]"
                  :items="slotProps.item.feedbacks"
                >
                  <template #created_at="{ item }">
                    {{ formatDate(item?.created_at) ?? 'N/A' }}
                  </template>
                  <template #status="{ item }">
                    <VChip
                      variant="tonal"
                      class="text-capitalize"
                      :color="resolveFeedbackAvatar(item.status).color"
                    >
                      <VAvatar
                        :icon="resolveFeedbackAvatar(item.status).icon"
                        :color="resolveFeedbackAvatar(item.status).color"
                      />
                      <span
                        class="text-capitalize "
                        style="padding-inline-start: 5px;"
                      >
                        {{ item.status }}
                      </span>
                    </VChip>
                  </template>
                  <template #actions="{ item }">
                    <IconBtn @click="router.push({ name: 'app-feedback-feedback-panel', query: { feedback: item.id } })">
                      <VIcon icon="tabler-eye" />
                    </IconBtn>
                  </template>
                </Table>
              </VCard>
              <VCard
                v-else
                variant="flat"
                class="my-8 mx-10"
              >
                <VCardText class="text-center">
                  No Feedback Collected
                </VCardText>
              </VCard>
            </td>
          </tr>
        </template>
        <template #item.wa_invitation="{ item }">
          <VChip
            v-if="item.feedback_invited"
            variant="tonal"
            color="success"
          >
            <VAvatar
              icon="tabler-circle-check"
              color="success"
            />
            <span
              class="text-capitalize"
              style="padding-inline-start: 5px;"
            >
              {{ formatDate(item.feedback_invitation_sent_at) }}
            </span>
          </VChip>
          <VChip
            v-else
            variant="tonal"
            color="error"
          >
            <VAvatar
              icon="tabler-circle-x"
              color="error"
            />
            <span
              class="text-capitalize"
              style="padding-inline-start: 5px;"
            >
              N/A
            </span>
          </VChip>
        </template>
        <template #item.patient="{ item }">
          <VMenu
            v-if="item.customer"
            open-on-hover
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <VChip v-bind="props">
                {{ item.customer.name }}
              </VChip>
            </template>
            <VCard max-width="350">
              <VList>
                <VListItem class="mx-0">
                  <template #title>
                    <VListItemTitle class="text-h6 text-primary">
                      {{ item.customer.code }}
                    </VListItemTitle>
                  </template>
                  <template #subtitle>
                    <template v-if="item.customer?.uhid">
                      <VListItemTitle>
                        UHID:
                        {{ item.customer.uhid }}
                      </VListItemTitle>
                    </template>
                    <template v-if="item.customer?.name">
                      <VListItemTitle>
                        Name:
                        {{ item.customer.name }}
                      </VListItemTitle>
                    </template>
                    <template v-if="item.customer?.phone">
                      <VListItemTitle>
                        Phone No:
                        {{ item.customer.phone }}
                      </VListItemTitle>
                    </template>
                    <template v-if="item.customer?.email">
                      <VListItemTitle>
                        Email:
                        {{ item.customer.email }}
                      </VListItemTitle>
                    </template>
                  </template>
                </VListItem>
              </VList>
              <VDivider />
            </VCard>
          </VMenu>
        </template>
        <template #item.visit_date_time="{ item }">
          {{ formatDate(item.visit_date_time) }}
        </template>
        <template #item.discharge_date="{ item }">
          {{ formatDate(item.discharge_date) }}
        </template>

        <!-- Action -->
        <template #item.action="{ item }">
          <VBtn
            variant="tonal"
            size="small"
            @click="() => {
              isFeedbackCollectDialog = true
              feedbackCollectForm.episode = item
              feedbackCollectForm.form_type_id = item.form_type_id
              feedbackCollectForm.location_id = item.location_id
            }" 
          >
            Collect Feedback
          </VBtn>
        </template>
        <template #item.view="{ item }">
          <IconBtn @click="getCustomPropertiesByEpisodeId(item.id)">
            <VIcon icon="tabler-eye" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalCategories) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalCategories / itemsPerPage)"
          :total-visible="5"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>
              
          <template #item="slotProps">
          <VBtn
          variant="outlined"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          {{ slotProps.page }}
          </VBtn>
          </template> 
             
          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>
    <EpisodeDrawer
      v-model:is-drawer-open="isEpisodeDrawerVisible"
      :custom-properties-data="customPropertiesTableData"
      :patient-details-data="patienDetailsTableData"
      :episode-details-data="episodeDetaisTableData"
      @close="isEpisodeDrawerVisible = false"
    />
  </section>

  <!-- Import Modal -->
  <VDialog
    v-model="isImportModal"
    max-width="600"
    :persistent="isImportLoading || isImportProcessing"
  >
    <DialogCloseBtn
      size="small"
      :disabled="isImportLoading || isImportProcessing"
      @click="() => (isImportModal = !isImportModal)"
    />
    <VCard>
      <VCardText>
        <h6 class="text-lg font-weight-medium">
          Import Episode
        </h6>
      </VCardText>

      <VCardText>
        <VForm ref="refImportForm">
          <VFileInput
            label="Upload File"
            prepend-icon="tabler-file-import"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            :disabled="isImportLoading || isImportProcessing"
            :error-messages="importErrors?.file"
            @change="
              (e) => {
                importFile = e.target.files[0];
              }
            "
          />
        </VForm>
      </VCardText>

      <!-- Errors -->
      <VCardText v-if="importErrors?.message">
        <VAlert
          color="error"
          variant="tonal"
          closable
        >
          <template #text>
            {{ importErrors.message }}
          </template>
        </VAlert>
      </VCardText>

      <!-- Validation Errors -->
      <VCardText v-if="importErrors && importErrors.length > 0">
        <div class="d-flex flex-column gap-2">
          <VAlert
            v-for="(error, index) in importErrors"
            :key="index"
            color="error"
            variant="tonal"
            closable
          >
            <!--
              <template #title>
              <div class="d-flex flex-column">
              <span class="text-h6 font-weight-bold error--text">
              {{ error.title }}
              </span>
              <span class="text-h6">{{ error.message }}</span>
              </div>
              </template> 
            -->
            <template #text>
              {{ error }}
            </template>
          </VAlert>
        </div>
      </VCardText>

      <!-- Progress Bar -->
      <VCardText v-if="isImportProcessing || importStatus.progress">
        <VProgressLinear
          v-if="importStatus.progress"
          height="4"
          buffer-value="0"
          color="primary"
          :model-value="importStatus.progress"
          stream
          rounded
        />
        <VProgressLinear
          v-else
          color="primary"
          height="4"
          indeterminate
          rounded
        />
      </VCardText>

      <!-- Action -->
      <VCardText>
        <div class="d-flex gap-2">
          <VBtn
            class="me-auto"
            variant="tonal"
            color="secondary"
            :disabled="isImportLoading || isImportProcessing"
            @click="downloadSampleFile"
          >
            Download Sample Excel
          </VBtn>
          <VBtn
            variant="tonal"
            color="error"
            :disabled="isImportLoading || isImportProcessing"
            @click="isImportModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="tonal"
            :disabled="isImportLoading || isImportProcessing"
            @click="submitImport"
          >
            Import
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Feedback Collect Dialog -->
  <VDialog
    v-model="isFeedbackCollectDialog"
    max-width="600"
  >
    <DialogCloseBtn
      size="small"
      @click="() => (isFeedbackCollectDialog = !isFeedbackCollectDialog)"
    />

    <VCard
      title="Feedback Collect" 
      :loading="sourceTypesLoading"
      :disabled="sourceTypesLoading"
    >
      <VDivider class="mt-4" />

      <VCardText>
        <div>
          <div class="text-h6">
            Feedback Type
          </div>
          <div class="text-base">
            Select feedback type
          </div>
        </div>
        <CustomRadiosWithOutLabel
          v-model:selected-radio="feedbackCollectForm.feedback_type"
          :radio-content="[
            {
              title: 'Standard',
              value: 'standard',
            },
            {
              title: 'Assorted',
              value: 'assorted',
            }
          ]"
          :grid-column="{cols:6,lg:4, md:4, sm:4, xs:4}"
          class="mt-4"
          :rules="[requiredValidator]"
        >
          <template #content="{ item }">
            <h6 class="text-base font-weight-medium">
              {{ item.title }}
            </h6>
          </template>
        </CustomRadiosWithOutLabel>
      </VCardText>

      <VDivider class="my-4" />

      <VCardText>
        <div>
          <div class="text-h6">
            Source Type
          </div>
          <div class="text-base">
            Select source type
          </div>
        </div>
        <CustomRadiosWithOutLabel
          v-model:selected-radio="feedbackCollectForm.source_type_id"
          :radio-content="updateObjectAsSelect(sourceTypes)"
          :grid-column="{cols:6,lg:4, md:4, sm:4, xs:4}"
          class="mt-4"
          :rules="[requiredValidator]"
        >
          <template #content="{ item }">
            <VImg
              v-if="item.avatar"
              :width="150"
              :height="150"
              :src="item.avatar"
              alt="bg-img"
            >
              <template #error>
                <VIcon
                  icon="tabler-alert-triangle"
                  size="2x"
                  class="text-danger"
                />
              </template>
            </VImg>
            <h6 class="text-base font-weight-medium">
              {{ item.title }}
            </h6>
          </template>
        </CustomRadiosWithOutLabel>
      </VCardText>

      <VDivider class="my-4" />

      <VCardText>
        <div>
          <div class="text-h6">
            Form Type
          </div>
          <div class="text-base">
            Select form type
          </div>
        </div>
        <CustomRadiosWithOutLabel
          v-model:selected-radio="feedbackCollectForm.form_type_id"
          :radio-content="updateObjectAsSelect(formTypes)"
          :grid-column="{cols:6,lg:4, md:4, sm:4, xs:4}"
          class="mt-4"
          :rules="[requiredValidator]"
        >
          <template #content="{ item }">
            <VImg
              v-if="item.avatar"
              :width="150"
              :height="150"
              :src="item.avatar"
              alt="bg-img"
            >
              <template #error>
                <VIcon
                  icon="tabler-alert-triangle"
                  size="2x"
                  class="text-danger"
                />
              </template>
            </VImg>
            <h6 class="text-base font-weight-medium">
              {{ item.title }}
            </h6>
          </template>
        </CustomRadiosWithOutLabel>
      </VCardText>

      <div v-if="!feedbackCollectForm?.episode?.location_id">
        <VDivider class="my-4" />

        <VCardText>
          <div class="mb-2">
            <div class="text-h6">
              Location
            </div>
            <div class="text-base">
              Select location
            </div>
          </div>
          <AppAutocomplete
            v-model="feedbackCollectForm.location_id"
            :loading="locationLoading"
            :items="locations"
            :item-title="item => item.name"
            :item-value="item => item.id"
            eager
            :return-object="false"
            placeholder="Search Location"
            :menu-props="{ maxHeight: '200px' }"
            :rules="[customRequiredValidator]"
            :required="isPatient"
          />
        </VCardText>
      </div>

      <div>
        <VDivider class="my-4" />

        <VCardText>          
          <div class="d-flex flex-wrap gap-4">
            <AppAutocomplete
              v-model="feedbackCollectForm.notification_template_id"
              :loading="notificationTemplateLoading"
              :items="notificationTemplates"
              :item-title="item => item.key"
              :item-value="item => item.id"
              :return-object="false"
              label="Notification Template"
              placeholder="Select Notification Template"
              :rules="[customRequiredValidator]"
              :required="isPatient"
            />
            <VBtn
              color="success"
              block
              append-icon="tabler-brand-whatsapp"
              @click="sendWhatsAppInvitation"
            >
              Send WhatsApp Feedback Invitation 
            </VBtn>
            <VAlert
              border="start"
              border-color="deep-blue accent-4"
              variant="tonal"
              color="primary"
            >
              <span class="text-h6">
                NOTE: For WhatsApp feedback invitations, feedback source type is automatically set to WhatsApp.
              </span>
            </VAlert>
          </div>
        </VCardText>
      </div>

      <div v-if="feedbackCollectForm.feedback_type === 'standard'">
        <VDivider class="my-4" />
        
        <VCardText v-if="qrCodeLink">
          <div class="d-flex flex-wrap justify-center border rounded">
            <QRCode
              ref="qrCodeRef"
              :value="qrCodeLink"
              :options="{
                errorCorrectionLevel: 'Q',
              }"
              style="block-size: 300px; inline-size: 300px;"
              @ready="onQRReady"
            />
          </div>
          <VBtn
            class="mt-4"
            block
            @click="downloadQRCode"
          >
            Download QR Code
          </VBtn>
        </VCardText>
        <VCardText v-else>
          <VAlert
            border="start"
            border-color="deep-blue accent-4"
            variant="tonal"
            color="primary"
          >
            <span class="text-h6">
              Fill all the details to see the qr code.
            </span>
          </VAlert>
        </VCardText>
      </div>

      <VDivider class="my-4" />

      <VCardText class="mb-4">
        <div class="d-flex flex-wrap gap-2">
          <VBtn
            color="success"
            block
            append-icon="tabler-external-link"
            @click="submitFeedbackCollect"
          >
            Collect Feedback
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped>
  .level-bg-color {
    background-color: #f7f7f7;
  }
</style>
